//Functions
import * as Functions from '../functions/common';
import { ChartPaymentDate } from '../models/Entities/CashFlow/Payment/ChartPaymentDate';
import { NewPayment } from '../models/Entities/CashFlow/Payment/NewPayment';

// models
import { Payment } from '../models/Entities/CashFlow/Payment/Payment';
import { PaymentImputation } from '../models/Entities/CashFlow/Payment/PaymentImputation';
import { NewPaymentImputation } from '../models/Entities/CashFlow/Payment/NewPaymentImputation';
import { IResult, ISimpleResult } from '../models/HttpResult';

// urls
const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'cashflow/payments';

// Get all payments by payer
export const GetPaymentsByPayer = async (token: string, payerIdentifier: string, receiverIdentifier: string | undefined): Promise<Payment[]> => {

    let RequestURL: string = BaseURL + '?payer=' + payerIdentifier;

    if (receiverIdentifier) {
        RequestURL += '&receiver=' + receiverIdentifier;
    }

    try {
        const Response: Payment[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<Payment[]>>;
            }
        })
        .then((data: IResult<Payment[]>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Get all payments by receiver
export const GetPaymentsByReceiver = async (token: string, receiverIdentifier: string, payerIdentifier: string | undefined): Promise<Payment[]> => {

    let RequestURL: string = BaseURL + '?receiver=' + receiverIdentifier;

    if (payerIdentifier) {
        RequestURL += '&payer=' + payerIdentifier;
    }

    try {
        const Response: Payment[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<Payment[]>>;
            }
        })
        .then((data: IResult<Payment[]>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Get payment by id
export const GetPaymentById = async (token: string, paymentId: number): Promise<Payment> => {

    let RequestURL: string = BaseURL + '/' + paymentId;

    try {
        const Response: Payment = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<Payment>>;
            }
        })
        .then((data: IResult<Payment>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Create payment
export const CreatePayment = async (token: string, payment: NewPayment): Promise<Payment> => {

    try {
        const Response: Payment = await fetch(BaseURL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify(payment)
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<Payment>>;
            }
        })
        .then((data: IResult<Payment>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Update payment
export const UpdatePayment = async (token: string, payment: Payment): Promise<Payment> => {

    try {
        const Response: Payment = await fetch(BaseURL + '/' + payment.id, {
            method: 'PUT',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify(payment)
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<Payment>>;
            }
        })
        .then((data: IResult<Payment>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Delete payment
export const DeletePayment = async (token: string, paymentId: number): Promise<Payment> => {

    try {
        const Response: Payment = await fetch(BaseURL + '/' + paymentId, {
            method: 'DELETE',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<Payment>>;
            }
        })
        .then((data: IResult<Payment>) => {
            return data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Get payment imputations by payment id
export const GetPaymentImputations = async (token: string, paymentId: number): Promise<PaymentImputation[]> => {

    const RequestURL: string = BaseURL + '/' + paymentId + '/imputations';

    try {
        const Response: PaymentImputation[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<PaymentImputation[]>>;
            }
        })
        .then((data: IResult<PaymentImputation[]>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Rollback payment imputation by imputation id
export const RollbackPaymentImputation = async (token: string, imputationId: number): Promise<ISimpleResult> => {

    const RequestURL: string = BaseURL + '/imputations/' + imputationId + '/rollback';

    try {
        const Response: ISimpleResult = await fetch(RequestURL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<ISimpleResult>;
            }
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

// Apply the payment's pending (unimputed) amount to debts.
// Uses apply-imputation, not adjustment: adjustment rolls back every imputation and rewrites the payment amount.
// Each item's amount must be in the PAYMENT currency (the API applies amount / exchangeRate to the debt).
export const CreatePaymentImputation = async (token: string, newImputation: NewPaymentImputation): Promise<PaymentImputation> => {

    const RequestURL: string = BaseURL + '/' + newImputation.paymentId + '/apply-imputation';

    try {
        const Response: PaymentImputation = await fetch(RequestURL, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            },
            body: JSON.stringify({
                debtsPaymentImputation: newImputation.debtsPaymentImputation
            })
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<PaymentImputation>>;
            }
        })
        .then((data: IResult<PaymentImputation>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

export enum PaymentType {
    income = 1,
    outcome = 2
}

// Get chart information 
export const GetPaymentsChart = async (paymentType: PaymentType, entityIdentifier: string, relatedEntityIdentifier: string | undefined, startDate: Date, EndDate: Date, token: string): Promise<ChartPaymentDate[]> => {

    let RequestURL: string = BaseURL + '/chart-info?PaymentType=' + paymentType + '&EntityIdentifier=' + entityIdentifier + (relatedEntityIdentifier ? '&RelatedEntityIdentifier=' + relatedEntityIdentifier : '') + '&StartDate=' + startDate.toJSON() + '&EndDate=' + EndDate.toJSON();

    try {
        const Response: ChartPaymentDate[] = await fetch(RequestURL, {
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token
            }
        })
        .then((response) => {
            if (!response.ok) {
                throw response;
            } else {
                return response.json() as Promise<IResult<ChartPaymentDate[]>>;
            }
        })
        .then((data: IResult<ChartPaymentDate[]>) => {
            return data.data;
        })
        .catch((error: any) =>
            error.text().then((body: any) => {
                throw Functions.HttpErrorHandler(body, error);
            })
        );

        return Response;
    } catch (error: any) {
        throw error;
    }
};

